/*!
 * © 2026. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For more information visit: https://egtuniverse.com/legal
 *
 * Created for Universe App Tools.
 */

var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "ua.text.bygroup",
  title: "Form Group: Grouped Text",
  description: "A text input that formats values into configured groups while preserving normalized saved data.",
  control: true,
  preview: "ua.preview.text",
  prevalidate: true,
  supportServer: false,
  fields: {
    "name": {
      title: "Name",
      type: "string",
      description: "The name of the control.",
      required: true
    },
    "title": {
      title: "Title",
      type: "string",
      description: "The title of the control.",
      required: true
    },
    "icon": {
      title: "Icon",
      type: "string",
      description: "The icon of the control.",
      required: false
    },
    "groups": {
      title: "Groups",
      type: "string",
      description: "Group lengths as text, like 3-3-3-3.",
      default: "3-3-3-3"
    },
    "separator": {
      title: "Separator",
      type: "string",
      description: "Group separator character.",
      default: "-"
    },
    "mode": {
      title: "Mode",
      type: "string",
      description: "Allowed character set mode.",
      default: "digits",
      list: {
        suggest: ["digits", "letters", "alphanumeric", "any"]
      }
    },
    "strictgroups": {
      title: "Strict Groups",
      type: "boolean",
      description: "Requires the full grouped length before save succeeds.",
      default: false
    },
    "storegrouped": {
      title: "Store Grouped",
      type: "boolean",
      description: "Stores grouped text instead of normalized text.",
      default: false
    },
    "value": {
      title: "Value",
      type: "string",
      description: "Initial value.",
      required: false
    },
    "inputclass": {
      title: "Input Class",
      type: "string",
      description: "The class of the input."
    },
    "inputstyle": {
      title: "Input Style",
      type: "string",
      description: "The style of the input."
    },
    "description": {
      title: "Description",
      type: "inner",
      description: "Helpful text shown below the input."
    }
  },
  passthrough: {
    except: ["inner", "id", "inputclass", "inputstyle", "groups", "separator", "mode", "strictgroups", "storegrouped"]
  }
});

var jsonRender = require("../interface/jsonRender.js");
var uai = require("../interface/interface.js");

function parsePlaceholderGroups(placeholder) {
  var raw = (placeholder || "").toString();
  if (!(raw)) return [];
  var parts = raw.match(/[a-z0-9]+/gi) || [];
  return parts.map(function(part) {
    return part.length;
  }).filter(function(len) {
    return Number.isFinite(len) && len > 0;
  });
}

function parseGroups(raw, placeholder, mode) {
  var values = (raw || "").toString().split(/[^0-9]+/g).filter(Boolean).map(function(part) {
    return Number(part);
  }).filter(function(n) {
    return Number.isFinite(n) && n > 0;
  });

  if (values.length === 0) {
    values = parsePlaceholderGroups(placeholder);
  }

  if (values.length === 0) {
    if ((mode || "").toString().toLowerCase() === "digits") return [4, 4, 4];
    return [3, 3, 3, 3];
  }
  return values;
}

function getGroupMax(groups) {
  return groups.reduce(function(total, part) {
    return total + Number(part || 0);
  }, 0);
}

function sanitizeValue(value, mode) {
  var text = (value || "").toString();
  var currentMode = (mode || "digits").toString().toLowerCase();
  if (currentMode === "letters") return text.replace(/[^a-z]/gi, "");
  if (currentMode === "alphanumeric") return text.replace(/[^a-z0-9]/gi, "");
  if (currentMode === "any") return text;
  return text.replace(/[^0-9]/g, "");
}

function normalizeRaw(value, groups, mode) {
  var max = getGroupMax(groups);
  var cleaned = sanitizeValue(value, mode);
  return cleaned.slice(0, max);
}

function formatByGroups(raw, groups, separator) {
  var sep = (separator || "-").toString();
  var formatted = [];
  var index = 0;

  for (var i = 0; i < groups.length; i++) {
    var size = Number(groups[i] || 0);
    if (!(size > 0)) continue;
    if (index >= raw.length) break;
    formatted.push(raw.slice(index, index + size));
    index += size;
  }

  return formatted.join(sep);
}

function toBoolean(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  if (typeof value === "number") return value === 1;
  return false;
}

function resolveConfigAttr(control, input, attrName, fallback) {
  var value = "";

  if (control && control.getAttribute) {
    value = control.getAttribute(attrName) || "";
  }

  if (!(value) && input && input.getAttribute) {
    value = input.getAttribute(attrName) || "";
  }

  if (!(value) && control && control.querySelector) {
    var holder = control.querySelector("[" + attrName + "]");
    if (holder && holder.getAttribute) value = holder.getAttribute(attrName) || "";
  }

  if (value === "") return fallback || "";
  return value;
}

function getControlConfig(control) {
  var input = control && control.querySelector ? control.querySelector("input") : null;
  var groups = parseGroups(
    resolveConfigAttr(control, input, "data-ua-groups", ""),
    resolveConfigAttr(control, input, "data-ua-placeholder", ""),
    resolveConfigAttr(control, input, "data-ua-mode", "digits")
  );
  return {
    groups: groups,
    separator: resolveConfigAttr(control, input, "data-ua-separator", "-"),
    mode: resolveConfigAttr(control, input, "data-ua-mode", "digits"),
    strictgroups: toBoolean(resolveConfigAttr(control, input, "data-ua-strictgroups", "false")),
    storegrouped: toBoolean(resolveConfigAttr(control, input, "data-ua-storegrouped", "false"))
  };
}

function updateInputValue(input, config) {
  var raw = normalizeRaw(input.value, config.groups, config.mode);
  input.value = formatByGroups(raw, config.groups, config.separator);
  return raw;
}

exports.render = async function(options) {
  var randomName = uai.generateRandomID(12);
  var groups = parseGroups(options.groups, options.placeholder, options.mode);
  var separator = (options.separator || "-").toString();
  var mode = (options.mode || "digits").toString().toLowerCase();
  var strictgroups = toBoolean(options.strictgroups);
  var storegrouped = toBoolean(options.storegrouped);
  var maxRaw = getGroupMax(groups);
  var maxLength = maxRaw + Math.max(0, groups.length - 1) * separator.length;

  var inputSpec = {
    "n": "input",
    "id": randomName,
    "class": "form-control",
    "title": options.title
  };

  inputSpec = jsonRender.pass({ ...options }, { ...inputSpec }, ["name", "class", "icon", "style"]);
  inputSpec.type = "text";
  inputSpec.maxlength = maxLength;

  if ("inputclass" in options) inputSpec.class += " " + options.inputclass;
  if ("inputstyle" in options) inputSpec.style = options.inputstyle;

  var element = {
    "n": "div",
    "name": options.name,
    "c": "form-group pb-3",
    "i": [{
      "n": "label",
      "for": randomName,
      "c": "pb-2 fs-4",
      "i": options.title
    }, {
      "n": "div",
      "c": "input-group",
      "i": ""
    }]
  };

  if ("icon" in options) {
    element.i[1].i = [{
      "n": "div",
      "c": "input-group-prepend",
      "i": [{
        "n": "div",
        "c": "input-group-text rounded-0 rounded-start input-icon-block",
        "i": [{
          "n": "i",
          "class": options.icon
        }]
      }]
    }, inputSpec];
  } else {
    element.i[1].i = inputSpec;
  }

  if ("description" in options) {
    element.i.push({
      "n": "div",
      "id": randomName + "-description",
      "c": "form-text",
      "i": options.description
    });
  }

  var domElement = await jsonRender.render(element);
  var input = domElement.querySelector("input");

  domElement.setAttribute("data-ua-groups", groups.join("-"));
  domElement.setAttribute("data-ua-placeholder", (options.placeholder || "").toString());
  domElement.setAttribute("data-ua-separator", separator);
  domElement.setAttribute("data-ua-mode", mode);
  domElement.setAttribute("data-ua-strictgroups", strictgroups ? "true" : "false");
  domElement.setAttribute("data-ua-storegrouped", storegrouped ? "true" : "false");
  input.setAttribute("data-ua-groups", groups.join("-"));
  input.setAttribute("data-ua-placeholder", (options.placeholder || "").toString());
  input.setAttribute("data-ua-separator", separator);
  input.setAttribute("data-ua-mode", mode);
  input.setAttribute("data-ua-strictgroups", strictgroups ? "true" : "false");
  input.setAttribute("data-ua-storegrouped", storegrouped ? "true" : "false");

  input.addEventListener("keydown", function(event) {
    if ((event.key || "") === separator) {
      event.preventDefault();
    }
  });

  input.addEventListener("input", function() {
    updateInputValue(input, {
      groups: groups,
      separator: separator,
      mode: mode
    });

    var uaeElement = input.closest("uae");
    if (uaeElement) save(options.name, uaeElement, {});
  });

  if ("value" in options) {
    var initialRaw = normalizeRaw(options.value, groups, mode);
    input.value = formatByGroups(initialRaw, groups, separator);
  }

  return domElement;
};

async function save(name, control, repo) {
  var input = control.querySelector("input");
  var config = getControlConfig(control);
  var raw = updateInputValue(input, config);
  var maxRaw = getGroupMax(config.groups);
  var errors = [];

  control.classList.remove("is-invalid");
  control.classList.remove("is-valid");

  if (!(input.checkValidity())) {
    errors.push({
      type: "validation",
      message: input.validationMessage
    });
  }

  if (config.strictgroups && raw.length > 0 && raw.length !== maxRaw) {
    errors.push({
      type: "validation",
      message: "Please enter the full value."
    });
  }

  repo.errors = errors;
  repo.success = errors.length === 0;
  control.classList.add(repo.success ? "is-valid" : "is-invalid");
  repo.data = config.storegrouped ? input.value : raw;

  return repo;
}
module.exports.save = save;

async function load(name, control, data) {
  var input = control.querySelector("input");
  var config = getControlConfig(control);
  var raw = normalizeRaw(data, config.groups, config.mode);
  input.value = formatByGroups(raw, config.groups, config.separator);
}
module.exports.load = load;
